import { Box, Text, Button, Flex, Badge, HStack, Icon, Divider, Tag } from '@chakra-ui/react';
import { InfoIcon, TimeIcon, WarningIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import * as routesNames from '../../../constants/routes';
import { AssetNotification } from '../../../hooks/useAssetNotification';

interface Props {
    notification: AssetNotification;
    onDismiss: () => void;
    bottom?: string;
}

const typeConfig: Record<string, {
    icon: any;
    iconColor: string;
    accentColor: string;
    badgeLabel: string;
    badgeBg: string;
}> = {
    new_request: {
        icon: InfoIcon,
        iconColor: '#2563eb',
        accentColor: '#2563eb',
        badgeLabel: '📦 New Asset Request',
        badgeBg: '#eff6ff'
    },
    return_due_today: {
        icon: WarningIcon,
        iconColor: '#dc2626',
        accentColor: '#dc2626',
        badgeLabel: '⚠️ Overdue / Due Today',
        badgeBg: '#fef2f2'
    },
    return_approaching: {
        icon: TimeIcon,
        iconColor: '#d97706',
        accentColor: '#d97706',
        badgeLabel: '📅 Return Approaching',
        badgeBg: '#fffbeb'
    },
    recovery_pending: {
        icon: WarningIcon,
        iconColor: '#dc2626',
        accentColor: '#dc2626',
        badgeLabel: '💰 Asset Recovery Pending',
        badgeBg: '#fef2f2'
    },
};

const fmt = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AssetNotificationPopup = ({ notification, onDismiss, bottom = '160px' }: Props) => {
    const navigate = useNavigate();
    const cfg = typeConfig[notification.type] ?? typeConfig['new_request'];
    const r = notification.reminder;

    return (
        <Box
            position="fixed"
            bottom={bottom}
            right="24px"
            zIndex={9998}
            bg="white"
            borderRadius="14px"
            boxShadow="0 8px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)"
            p={4}
            w="360px"
            borderLeft={`4px solid ${cfg.accentColor}`}
            animation="assetSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)"
            sx={{
                '@keyframes assetSlideIn': {
                    from: { transform: 'translateX(120%)', opacity: 0 },
                    to: { transform: 'translateX(0)', opacity: 1 }
                }
            }}
        >
            {/* Header */}
            <Flex justify="space-between" align="flex-start" mb={2}>
                <HStack spacing={2}>
                    <Icon as={cfg.icon} color={cfg.iconColor} boxSize={4} />
                    <Text fontWeight="700" fontSize="xs" color="gray.700" textTransform="uppercase" letterSpacing="0.5px">
                        Asset Alert
                    </Text>
                </HStack>
                <Button
                    size="xs" variant="ghost" onClick={onDismiss}
                    color="gray.400" _hover={{ color: 'gray.600', bg: 'gray.100' }}
                    minW="auto" p={1} borderRadius="full"
                >
                    ✕
                </Button>
            </Flex>

            {/* Badge */}
            <Badge px={2.5} py={1} borderRadius="full" fontSize="10px" fontWeight="bold"
                bg={cfg.badgeBg} color={cfg.iconColor} mb={2.5}>
                {cfg.badgeLabel}
            </Badge>

            {/* ── Return reminder detail view ── */}
            {r ? (
                <>
                    {/* Employee + Asset */}
                    <Box bg="gray.50" borderRadius="lg" p={3} mb={3}>
                        <Flex justify="space-between" align="flex-start">
                            <Box flex={1}>
                                <Text fontWeight="700" fontSize="sm" color="gray.800" mb={0.5}>
                                    {r.employeeName}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    {r.assetName}
                                    {r.assetBrand ? ` · ${r.assetBrand}` : ''}
                                </Text>
                                <Text fontSize="10px" color="gray.400" mt={0.5}>
                                    {r.assetCode}
                                    {r.subCategoryName ? ` · ${r.subCategoryName}` : ` · ${r.categoryName}`}
                                </Text>
                            </Box>
                            {r.isOverdue ? (
                                <Tag size="sm" bg="#fef2f2" color="#dc2626" borderRadius="full" fontWeight="bold" fontSize="10px" whiteSpace="nowrap">
                                    {r.daysOverdue}d overdue
                                </Tag>
                            ) : r.isDueToday ? (
                                <Tag size="sm" bg="#fef2f2" color="#dc2626" borderRadius="full" fontWeight="bold" fontSize="10px">
                                    Due today
                                </Tag>
                            ) : (
                                <Tag size="sm" bg="#fffbeb" color="#d97706" borderRadius="full" fontWeight="bold" fontSize="10px" whiteSpace="nowrap">
                                    {r.daysLeft}d left
                                </Tag>
                            )}
                        </Flex>

                        <Divider my={2} />

                        <Flex align="center" gap={1}>
                            <Text fontSize="10px" color="gray.500" fontWeight="medium">Expected return:</Text>
                            <Text fontSize="10px" color={r.isOverdue ? '#dc2626' : '#d97706'} fontWeight="bold">
                                {fmt(r.expectedReturnDate)}
                            </Text>
                        </Flex>
                    </Box>
                </>
            ) : (
                /* ── Generic count notification ── */
                <>
                    <Text fontWeight="600" fontSize="sm" mb={1} color="gray.800" noOfLines={2}>
                        {notification.message}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        {notification.detail}
                    </Text>
                </>
            )}

            {/* Actions */}
            <Flex gap={2}>
                <Button
                    size="sm" bg={cfg.accentColor} color="white" flex={1}
                    borderRadius="full" _hover={{ opacity: 0.9 }} fontSize="xs"
                    onClick={() => {
                        navigate(
                            notification.type === 'new_request'
                                ? routesNames.ASSETASSIGNMENT
                                : notification.type === 'recovery_pending'
                                    ? routesNames.ASSETRECOVERY
                                    : routesNames.ASSETRETURN
                        );
                        onDismiss();
                    }}
                >
                    View Details
                </Button>
                <Button
                    size="sm" variant="outline" flex={1}
                    borderRadius="full" fontSize="xs" color="gray.500"
                    onClick={onDismiss}
                >
                    {notification.reminder ? 'Next →' : 'Dismiss'}
                </Button>
            </Flex>
        </Box>
    );
};

export default AssetNotificationPopup;